// frontend/src/pages/Discounts/Discounts.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit, Loader2, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import "../SettingsCrud/SettingsCrud.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForm = {
  name: "",
  code: "",
  description: "",
  discount_type: "percentage",
  value: "",
  applies_to: "both",
  start_date: "",
  end_date: "",
  status: "active",
  company_id: "",
};

export default function Discounts() {
  const { currentUser, userType } = useAuth();
  const normalizedRole = String(currentUser?.role || "").trim().toLowerCase();
  const isSystemAdmin =
    userType === "system" && ["developer", "admin"].includes(normalizedRole);

  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/discounts`, {
        headers: getAuthHeaders(),
        params: {
          status,
          search,
          ...(isSystemAdmin && selectedCompany
            ? { company_id: selectedCompany }
            : {}),
        },
      });
      setItems(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load discounts");
    } finally {
      setLoading(false);
    }
  }, [isSystemAdmin, search, selectedCompany, status]);

  useEffect(() => {
    const timer = window.setTimeout(fetchItems, 200);
    return () => window.clearTimeout(timer);
  }, [fetchItems]);

  useEffect(() => {
    if (!isSystemAdmin) return;
    axios
      .get(`${url}/api/company`, { headers: getAuthHeaders() })
      .then((response) => setCompanies(response.data?.data || []))
      .catch((error) =>
        toast.error(error.response?.data?.message || "Failed to load companies"),
      );
  }, [isSystemAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, company_id: selectedCompany });
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      code: item.code || "",
      description: item.description || "",
      discount_type: item.discount_type || "percentage",
      value: String(item.value ?? ""),
      applies_to: item.applies_to || "both",
      start_date: item.start_date ? item.start_date.slice(0, 10) : "",
      end_date: item.end_date ? item.end_date.slice(0, 10) : "",
      status: item.status || "active",
      company_id: item.company_id?._id || item.company_id || "",
    });
    setErrors({});
    setShowModal(true);
  };

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    const value = Number(form.value);

    if (form.name.trim().length < 2) next.name = "Name is required";
    if (!form.code.trim()) next.code = "Code is required";
    if (!Number.isFinite(value) || value < 0) next.value = "Enter a valid value";
    if (form.discount_type === "percentage" && value > 100) {
      next.value = "Percentage cannot exceed 100";
    }
    if (isSystemAdmin && !form.company_id) {
      next.company_id = "Company is required";
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      next.end_date = "End date cannot be earlier than start date";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      ...form,
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      value: Number(form.value),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      ...(isSystemAdmin ? { company_id: form.company_id } : {}),
    };

    try {
      setSaving(true);
      if (editing) {
        await axios.put(`${url}/api/discounts/${editing._id}`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Discount updated successfully");
      } else {
        await axios.post(`${url}/api/discounts`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Discount created successfully");
      }
      setShowModal(false);
      await fetchItems();
    } catch (error) {
      const response = error.response?.data;
      toast.error(response?.message || "Failed to save discount");
      if (Array.isArray(response?.errors)) {
        setErrors(Object.fromEntries(response.errors.map((item) => [item.field, item.message])));
      }
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (item) => {
    if (!window.confirm(`Deactivate ${item.name}?`)) return;
    try {
      await axios.delete(`${url}/api/discounts/${item._id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Discount deactivated");
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to deactivate discount");
    }
  };

  const toggleStatus = async (item) => {
    try {
      const nextStatus = item.status === "active" ? "inactive" : "active";
      await axios.patch(
        `${url}/api/discounts/${item._id}/status`,
        { status: nextStatus },
        { headers: getAuthHeaders() },
      );
      toast.success(`Discount ${nextStatus === "active" ? "activated" : "deactivated"}`);
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change status");
    }
  };

  const summary = useMemo(() => ({
    total: items.length,
    active: items.filter((item) => item.status === "active").length,
  }), [items]);

  return (
    <main className="settings-crud-page">
      <header className="settings-crud-header">
        <div>
          <p className="settings-crud-eyebrow">Company settings</p>
          <h1>Discounts</h1>
          <p>{summary.total} records · {summary.active} active</p>
        </div>
        <button className="settings-crud-button settings-crud-button--primary" onClick={openCreate}>
          <Plus size={16} /> Add discount
        </button>
      </header>

      <section className="settings-crud-toolbar">
        <div className="settings-crud-search">
          <Search size={17} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code or description" />
        </div>
        {isSystemAdmin && (
          <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}>
            <option value="">All companies</option>
            {companies.map((company) => (
              <option key={company._id} value={company._id}>{company.company_name}</option>
            ))}
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button className="settings-crud-button settings-crud-button--secondary" onClick={fetchItems}>
          <RefreshCw size={16} /> Refresh
        </button>
      </section>

      <section className="settings-crud-card">
        <div className="settings-crud-table-wrap">
          <table className="settings-crud-table">
            <thead>
              <tr>
                <th>Name</th><th>Code</th><th>Type</th><th>Value</th><th>Applies to</th>
                {isSystemAdmin && <th>Company</th>}<th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isSystemAdmin ? 8 : 7}><div className="settings-crud-empty"><Loader2 className="settings-crud-spin" /> Loading…</div></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={isSystemAdmin ? 8 : 7}><div className="settings-crud-empty">No discounts found</div></td></tr>
              ) : items.map((item) => (
                <tr key={item._id}>
                  <td><strong>{item.name}</strong><div>{item.description || "—"}</div></td>
                  <td>{item.code}</td>
                  <td>{item.discount_type}</td>
                  <td>{item.discount_type === "percentage" ? `${item.value}%` : Number(item.value).toFixed(2)}</td>
                  <td>{item.applies_to}</td>
                  {isSystemAdmin && <td>{item.company_id?.company_name || "—"}</td>}
                  <td><button type="button" onClick={() => toggleStatus(item)} className={`settings-crud-badge settings-crud-badge--${item.status}`}>{item.status}</button></td>
                  <td><div className="settings-crud-actions">
                    <button onClick={() => openEdit(item)} title="Edit"><Edit size={15} /></button>
                    <button className="danger" onClick={() => deactivate(item)} disabled={item.status === "inactive"} title="Deactivate"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="settings-crud-footer">Showing {items.length} discount{items.length === 1 ? "" : "s"}</footer>
      </section>

      {showModal && (
        <div className="settings-crud-backdrop" onMouseDown={() => setShowModal(false)}>
          <section className="settings-crud-modal" onMouseDown={(e) => e.stopPropagation()}>
            <button className="settings-crud-close" onClick={() => setShowModal(false)}><X size={18} /></button>
            <h2>{editing ? "Edit discount" : "Add discount"}</h2>
            <form className="settings-crud-form" onSubmit={submit}>
              {isSystemAdmin && (
                <div className="settings-crud-field settings-crud-field--full">
                  <label>Company *</label>
                  <select value={form.company_id} onChange={(e) => setField("company_id", e.target.value)}>
                    <option value="">Select company</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>{company.company_name}</option>
                    ))}
                  </select>
                  {errors.company_id && <small className="settings-crud-error">{errors.company_id}</small>}
                </div>
              )}
              <div className="settings-crud-field"><label>Name *</label><input value={form.name} onChange={(e) => setField("name", e.target.value)} />{errors.name && <small className="settings-crud-error">{errors.name}</small>}</div>
              <div className="settings-crud-field"><label>Code *</label><input value={form.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} />{errors.code && <small className="settings-crud-error">{errors.code}</small>}</div>
              <div className="settings-crud-field"><label>Type</label><select value={form.discount_type} onChange={(e) => setField("discount_type", e.target.value)}><option value="percentage">Percentage</option><option value="fixed">Fixed</option></select></div>
              <div className="settings-crud-field"><label>Value *</label><input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setField("value", e.target.value)} />{errors.value && <small className="settings-crud-error">{errors.value}</small>}</div>
              <div className="settings-crud-field"><label>Applies to</label><select value={form.applies_to} onChange={(e) => setField("applies_to", e.target.value)}><option value="sale">Sale</option><option value="purchase">Purchase</option><option value="both">Both</option></select></div>
              <div className="settings-crud-field"><label>Status</label><select value={form.status} onChange={(e) => setField("status", e.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
              <div className="settings-crud-field"><label>Start date</label><input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} /></div>
              <div className="settings-crud-field"><label>End date</label><input type="date" value={form.end_date} onChange={(e) => setField("end_date", e.target.value)} />{errors.end_date && <small className="settings-crud-error">{errors.end_date}</small>}</div>
              <div className="settings-crud-field settings-crud-field--full"><label>Description</label><textarea value={form.description} onChange={(e) => setField("description", e.target.value)} /></div>
              <div className="settings-crud-form-actions">
                <button type="button" className="settings-crud-button settings-crud-button--secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="settings-crud-button settings-crud-button--primary" disabled={saving}>{saving && <Loader2 className="settings-crud-spin" size={16} />}{editing ? "Update" : "Create"}</button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}